import { PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  message,
  Row,
  Select,
  Space,
  Tag,
  TimePicker,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { ContactEditModal } from '@/modules/zy-crm/components/contact-edit-modal';
import { CustomerEditModal } from '@/modules/zy-crm/components/customer-edit-modal';
import { LeadEditModal } from '@/modules/zy-crm/components/lead-edit-modal';
import {
  FOLLOW_UP_CONTENT_LIMIT,
  plainTextToMarkdown,
} from '@/modules/zy-crm/components/markdown/content';
import {
  MarkdownEditor,
  type MarkdownEditorHandle,
} from '@/modules/zy-crm/components/markdown/editor';
import { OpportunityEditModal } from '@/modules/zy-crm/components/opportunity-edit-modal';
import { toApiDateTime } from '@/modules/zy-crm/utils/display';
import {
  FOLLOW_UP_GOAL_OPTIONS,
  FOLLOW_UP_QUALITY_SCORE_OPTIONS,
  FOLLOW_UP_RESULT_OPTIONS,
  FOLLOW_UP_TYPE_OPTIONS,
} from '@/modules/zy-crm/utils/follow-up';
import { getResponsiveModalWidth } from '@/modules/zy-crm/utils/modal-width';
import {
  fetchContactOptions,
  fetchCustomerOptions,
  fetchLeadOptions,
  fetchOpportunityOptions,
  mergeOptions,
  type RelationOption,
} from '@/modules/zy-crm/utils/relation-options';
import type { ContactItem } from '@/services/bff/types/contact';
import type { CustomerItem } from '@/services/bff/types/customer';
import type {
  FollowUpItem,
  FollowUpRelationItem,
  FollowUpRelationTargetType,
} from '@/services/bff/types/follow-up';
import type { LeadItem } from '@/services/bff/types/lead';
import { createAppRequest } from '@/services/request';
import { getCurrentMemberUuid } from '@/utils/currentMember';

import {
  primaryType,
  RELATION_LABELS,
  type RelationSelection,
  relationSelection,
  selectedRelations,
} from './relations';

const appRequest = createAppRequest();

type FollowUpCreateFormValues = RelationSelection & {
  followUpDate?: Dayjs;
  followUpTime?: Dayjs | null;
  followUpType?: number;
  goalTag?: string;
  resultTag?: string;
  content?: string;
  contentFormat?: 'plain-text' | 'markdown';
  qualityScore?: number;
  nextStepConfirmed?: boolean;
  nextStepContent?: string;
  nextStepAt?: Dayjs;
  nextTime?: Dayjs;
  stageAdvanced?: boolean;
  keySignal?: string;
  lostReasonHint?: string;
  leadUuids?: string[];
  customerUuids?: string[];
  contactUuids?: string[];
  opportunityUuids?: string[];
  syncOpportunityAdvance?: boolean;
  syncCurrentBlockers?: string;
  syncNextAction?: string;
  createFollowUpTask?: boolean;
};

type FollowUpCreateContext = {
  bizType: number;
  bizUuid: string;
  bizName?: string;
  targetType?: FollowUpRelationTargetType;
  defaultLeadUuids?: string[];
  defaultCustomerUuids?: string[];
  defaultContactUuids?: string[];
  defaultOpportunityUuids?: string[];
  defaultRelations?: FollowUpRelationItem[];
};

type FollowUpEditTarget = {
  uuid: string;
};

type FollowUpCreateModalProps = {
  open: boolean;
  title: string;
  context: FollowUpCreateContext;
  defaultValues?: Partial<FollowUpCreateFormValues>;
  editTarget?: FollowUpEditTarget;
  confirmLoading?: boolean;
  onCancel: () => void;
  onSuccess?: (values: FollowUpCreateFormValues) => Promise<void> | void;
};

const normalizeOptionalText = (value: unknown) => {
  const text = typeof value === 'string' ? value.trim() : '';
  return text || undefined;
};

const toIsoValue = (value?: Dayjs | null) => toApiDateTime(value);

const BIZ_TYPE_TO_TARGET_TYPE: Record<number, FollowUpRelationTargetType> = {
  1: 'lead',
  2: 'customer',
  3: 'contact',
  4: 'opportunity',
  5: 'contract',
  6: 'quote',
  7: 'receipt',
};

const FOLLOW_UP_MODAL_WIDTH = getResponsiveModalWidth('xlarge');
const RELATED_MODAL_WIDTH = getResponsiveModalWidth('large');

function ExtraRelationTags({
  value = [],
  onChange,
}: {
  value?: FollowUpRelationItem[];
  onChange?: (value: FollowUpRelationItem[]) => void;
}) {
  return (
    <Space wrap>
      {value.length ? (
        value.map((item) => (
          <Tag
            key={`${item.targetType}:${item.targetUuid}`}
            closable
            onClose={(event) => {
              event.preventDefault();
              onChange?.(value.filter((current) => current !== item));
            }}
          >
            {RELATION_LABELS[item.targetType]}：
            {item.targetName || item.targetUuid}
          </Tag>
        ))
      ) : (
        <span>—</span>
      )}
    </Space>
  );
}

const mergeCreatedOption = (
  options: RelationOption[],
  uuid?: string,
  label?: string,
) => {
  const safeUuid = String(uuid || '').trim();
  if (!safeUuid) return options;
  return mergeOptions(
    options,
    [{ value: safeUuid, label: String(label || safeUuid).trim() || safeUuid }],
    safeUuid,
  );
};

const appendFormUuid = (
  form: ReturnType<typeof Form.useForm<FollowUpCreateFormValues>>[0],
  field: keyof Pick<
    FollowUpCreateFormValues,
    'leadUuids' | 'customerUuids' | 'contactUuids' | 'opportunityUuids'
  >,
  uuid?: string,
) => {
  const safeUuid = String(uuid || '').trim();
  if (!safeUuid) return;
  const current = form.getFieldValue(field) || [];
  form.setFieldValue(field, Array.from(new Set([...current, safeUuid])));
};

export function FollowUpCreateModal({
  open,
  title,
  context,
  defaultValues,
  editTarget,
  confirmLoading,
  onCancel,
  onSuccess,
}: FollowUpCreateModalProps) {
  const [form] = Form.useForm<FollowUpCreateFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [detailError, setDetailError] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const dirty = useRef(false);
  const contentEditor = useRef<MarkdownEditorHandle>(null);
  const selectedLead = Form.useWatch('leadUuids', form);
  const selectedCustomer = Form.useWatch('customerUuids', form);
  const selectedContact = Form.useWatch('contactUuids', form);
  const selectedOpportunity = Form.useWatch('opportunityUuids', form);
  const selectedExtra = Form.useWatch('extraRelations', form);
  const original = useRef({
    bizType: context.bizType,
    bizUuid: context.bizUuid,
  });
  const selection = selectedRelations(
    {
      leadUuids: selectedLead,
      customerUuids: selectedCustomer,
      contactUuids: selectedContact,
      opportunityUuids: selectedOpportunity,
      extraRelations: selectedExtra,
    },
    original.current,
  );
  const fileTarget = {
    type: primaryType(selection.bizType),
    uuid: selection.bizUuid,
  };
  const handleCancel = () => {
    if (uploading || submitting) return;
    if (!dirty.current) {
      onCancel();
      return;
    }
    Modal.confirm({
      closable: { 'aria-label': '关闭' },
      keyboard: true,
      title: '放弃未保存的跟进内容？',
      okText: '放弃修改',
      cancelText: '继续编辑',
      onOk: () => {
        dirty.current = false;
        onCancel();
      },
    });
  };
  const [leadOptions, setLeadOptions] = useState<RelationOption[]>([]);
  const [customerOptions, setCustomerOptions] = useState<RelationOption[]>([]);
  const [contactOptions, setContactOptions] = useState<RelationOption[]>([]);
  const [opportunityOptions, setOpportunityOptions] = useState<
    RelationOption[]
  >([]);
  const [leadCreateOpen, setLeadCreateOpen] = useState(false);
  const [customerCreateOpen, setCustomerCreateOpen] = useState(false);
  const [contactCreateOpen, setContactCreateOpen] = useState(false);
  const [opportunityCreateOpen, setOpportunityCreateOpen] = useState(false);
  const currentMemberUuid = getCurrentMemberUuid() || null;

  const seedOptions = (relations: FollowUpRelationItem[]) => {
    for (const [type, setter] of [
      ['lead', setLeadOptions],
      ['customer', setCustomerOptions],
      ['contact', setContactOptions],
      ['opportunity', setOpportunityOptions],
    ] as const) {
      const named = relations
        .filter((item) => item.targetType === type && item.targetName)
        .map((item) => ({ value: item.targetUuid, label: item.targetName! }));
      setter((prev) => mergeOptions(prev, named));
    }
  };
  // Read defaults on opening/context changes, without resetting an in-progress draft on parent renders.
  const initial = useRef({ context, defaultValues });
  initial.current = { context, defaultValues };
  useEffect(() => {
    if (!open) return;
    const { context, defaultValues } = initial.current;
    original.current = { bizType: context.bizType, bizUuid: context.bizUuid };
    const type = context.targetType || primaryType(context.bizType);
    const relations: FollowUpRelationItem[] = [
      ...(context.defaultRelations || []),
    ];
    if (type && context.bizUuid)
      relations.push({
        targetType: type,
        targetUuid: context.bizUuid,
        targetName: context.bizName,
        relationRole: 'primary',
      });
    for (const [type, ids] of [
      ['lead', context.defaultLeadUuids],
      ['customer', context.defaultCustomerUuids],
      ['contact', context.defaultContactUuids],
      ['opportunity', context.defaultOpportunityUuids],
    ] as const)
      for (const uuid of ids || [])
        relations.push({ targetType: type, targetUuid: uuid });
    dirty.current = false;
    form.resetFields();
    form.setFieldsValue({
      followUpDate: dayjs().startOf('day'),
      followUpTime: dayjs().startOf('minute'),
      followUpType: 1,
      qualityScore: 3,
      nextStepConfirmed: true,
      stageAdvanced: false,
      syncOpportunityAdvance: false,
      createFollowUpTask: false,
      ...relationSelection(relations),
      ...defaultValues,
      content:
        defaultValues?.contentFormat === 'markdown'
          ? defaultValues.content
          : plainTextToMarkdown(defaultValues?.content),
    });
    seedOptions(relations);
  }, [open, context.bizType, context.bizUuid, editTarget?.uuid, form]);

  useEffect(() => {
    if (!open || !editTarget?.uuid) {
      return;
    }
    let active = true;
    setDetailError(false);
    setDetailLoading(true);
    void appRequest<FollowUpItem>(`/bff/zy-crm/follow-ups/${editTarget.uuid}`, {
      method: 'GET',
    })
      .then((detail) => {
        if (!active || !detail) return;
        original.current = {
          bizType: detail.bizType || context.bizType,
          bizUuid: detail.bizUuid || context.bizUuid,
        };
        const loadedRelations = [...(detail.relations || [])];
        const type = primaryType(detail.bizType);
        if (
          type &&
          detail.bizUuid &&
          !loadedRelations.some(
            (item) =>
              item.targetType === type && item.targetUuid === detail.bizUuid,
          )
        )
          loadedRelations.push({
            targetType: type,
            targetUuid: detail.bizUuid,
            targetName: detail.bizName,
            relationRole: 'primary',
          });
        seedOptions(loadedRelations);
        form.setFieldsValue({
          ...relationSelection(loadedRelations),
          followUpDate: detail.followUpDate
            ? dayjs(detail.followUpDate)
            : detail.createTime
              ? dayjs(detail.createTime).startOf('day')
              : undefined,
          followUpTime: detail.followUpDate
            ? detail.followUpTime
              ? dayjs(`${detail.followUpDate}T${detail.followUpTime}`)
              : null
            : detail.createTime
              ? dayjs(detail.createTime).startOf('minute')
              : null,
          followUpType: detail.followUpType,
          goalTag: detail.goalTag,
          resultTag: detail.resultTag,
          content:
            detail.contentFormat === 'markdown'
              ? detail.content
              : plainTextToMarkdown(detail.content),
          qualityScore: detail.qualityScore,
          nextStepConfirmed: detail.nextStepConfirmed,
          nextStepContent: detail.nextStepContent,
          nextStepAt: detail.nextStepAt ? dayjs(detail.nextStepAt) : undefined,
          nextTime: detail.nextTime ? dayjs(detail.nextTime) : undefined,
          stageAdvanced: detail.stageAdvanced,
          keySignal: detail.keySignal,
          lostReasonHint: detail.lostReasonHint,
          syncOpportunityAdvance: detail.syncOpportunityAdvance,
          syncCurrentBlockers: detail.syncCurrentBlockers,
          syncNextAction: detail.syncNextAction,
        });
      })
      .catch(() => {
        if (active) {
          setDetailError(true);
          message.error('跟进记录加载失败，请关闭后重试');
        }
      })
      .finally(() => {
        if (active) setDetailLoading(false);
      });
    return () => {
      active = false;
    };
  }, [editTarget?.uuid, form, open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const ensureContactValue = (context.defaultContactUuids || [])[0];
    const ensureCustomerValue = (context.defaultCustomerUuids || [])[0];
    const ensureLeadValue = (context.defaultLeadUuids || [])[0];
    const ensureOpportunityValue = (context.defaultOpportunityUuids || [])[0];
    void fetchLeadOptions(undefined).then((options) =>
      setLeadOptions((prev) => mergeOptions(options, prev, ensureLeadValue)),
    );
    void fetchCustomerOptions(undefined).then((options) =>
      setCustomerOptions((prev) =>
        mergeOptions(options, prev, ensureCustomerValue),
      ),
    );
    void fetchContactOptions(undefined).then((options) =>
      setContactOptions((prev) =>
        mergeOptions(options, prev, ensureContactValue),
      ),
    );
    void fetchOpportunityOptions(undefined).then((options) =>
      setOpportunityOptions((prev) =>
        mergeOptions(options, prev, ensureOpportunityValue),
      ),
    );
  }, [
    context.defaultContactUuids,
    context.defaultCustomerUuids,
    context.defaultLeadUuids,
    context.defaultOpportunityUuids,
    open,
  ]);

  const handleSubmit = async () => {
    if (uploading || detailLoading || detailError || submitting) return;
    const latestContent = contentEditor.current?.getMarkdown();
    if (latestContent !== undefined)
      form.setFieldValue('content', latestContent);
    const values = await form.validateFields();
    const resolved = selectedRelations(values, original.current);
    const { relations } = resolved;
    if (!relations.length) {
      message.warning('请至少关联一个业务对象');
      return;
    }
    setSubmitting(true);
    try {
      const saved = await appRequest<{ uuid?: string }>(
        editTarget
          ? `/bff/zy-crm/follow-ups/${editTarget.uuid}`
          : '/bff/zy-crm/follow-ups',
        {
          method: editTarget ? 'PATCH' : 'POST',
          data: {
            bizType: resolved.bizType,
            bizUuid: resolved.bizUuid,
            followUpDate: values.followUpDate?.format('YYYY-MM-DD'),
            followUpTime: values.followUpTime?.format('HH:mm') || null,
            content: values.content,
            contentFormat: 'markdown',
            nextTime: toIsoValue(values.nextTime),
            followUpType:
              values.followUpType === undefined || values.followUpType === null
                ? undefined
                : Number(values.followUpType),
            resultTag: normalizeOptionalText(values.resultTag),
            goalTag: normalizeOptionalText(values.goalTag),
            qualityScore:
              values.qualityScore === undefined || values.qualityScore === null
                ? undefined
                : Number(values.qualityScore),
            stageAdvanced: Boolean(values.stageAdvanced),
            nextStepConfirmed:
              values.nextStepConfirmed === undefined ||
              values.nextStepConfirmed === null
                ? undefined
                : Boolean(values.nextStepConfirmed),
            nextStepContent: normalizeOptionalText(values.nextStepContent),
            nextStepAt: toIsoValue(values.nextStepAt),
            keySignal: normalizeOptionalText(values.keySignal),
            lostReasonHint: normalizeOptionalText(values.lostReasonHint),
            contactUuids: (values.contactUuids || []).join(',') || undefined,
            opportunityUuids:
              (values.opportunityUuids || []).join(',') || undefined,
            relations,
            syncOpportunityAdvance: Boolean(values.syncOpportunityAdvance),
            syncCurrentBlockers: normalizeOptionalText(
              values.syncCurrentBlockers,
            ),
            syncNextAction: normalizeOptionalText(values.syncNextAction),
          },
        },
      );
      if (!editTarget && values.createFollowUpTask && values.nextStepContent) {
        const taskBizType = primaryType(resolved.bizType);
        const taskBizUuid = resolved.bizUuid;
        if (taskBizType && taskBizUuid) {
          await appRequest('/bff/zy-crm/tasks', {
            method: 'POST',
            data: {
              bizType: taskBizType,
              bizUuid: taskBizUuid,
              title: values.nextStepContent,
              dueTime: toIsoValue(values.nextStepAt || values.nextTime),
              sourceFollowUpUuid: saved?.uuid,
            },
          }).catch(() => {
            message.warning('跟进记录已保存，后续任务创建失败');
          });
        }
      }
      dirty.current = false;
      await onSuccess?.(values);
      const sourceType = primaryType(context.bizType);
      const moved =
        context.bizUuid &&
        !relations.some(
          (item) =>
            item.targetType === sourceType &&
            item.targetUuid === context.bizUuid,
        );
      const name =
        [
          ...leadOptions,
          ...customerOptions,
          ...contactOptions,
          ...opportunityOptions,
        ].find((item) => item.value === resolved.bizUuid)?.label ||
        resolved.bizUuid;
      message.success(
        moved
          ? `跟进已保存至${RELATION_LABELS[primaryType(resolved.bizType)!]}「${name}」，已取消来源关联`
          : editTarget
            ? '跟进记录已更新'
            : '跟进记录已创建',
      );
      form.resetFields();
    } finally {
      setSubmitting(false);
    }
  };

  const handleLeadCreated = (record?: LeadItem) => {
    setLeadCreateOpen(false);
    const uuid = String(record?.uuid || '').trim();
    if (!uuid) {
      message.warning('线索已创建，请在线索字段中搜索选择');
      void fetchLeadOptions(undefined).then(setLeadOptions);
      return;
    }
    setLeadOptions((prev) => mergeCreatedOption(prev, uuid, record?.name));
    appendFormUuid(form, 'leadUuids', uuid);
  };

  const handleCustomerCreated = (record?: CustomerItem) => {
    setCustomerCreateOpen(false);
    const uuid = String(record?.uuid || '').trim();
    if (!uuid) {
      message.warning('客户已创建，请在客户字段中搜索选择');
      void fetchCustomerOptions(undefined).then(setCustomerOptions);
      return;
    }
    setCustomerOptions((prev) => mergeCreatedOption(prev, uuid, record?.name));
    appendFormUuid(form, 'customerUuids', uuid);
  };

  const handleContactCreated = (record?: ContactItem) => {
    setContactCreateOpen(false);
    const uuid = String(record?.uuid || '').trim();
    if (!uuid) {
      message.warning('联系人已创建，请在联系人字段中搜索选择');
      void fetchContactOptions(undefined).then(setContactOptions);
      return;
    }
    setContactOptions((prev) => mergeCreatedOption(prev, uuid, record?.name));
    appendFormUuid(form, 'contactUuids', uuid);
  };

  const handleOpportunityCreated = () => {
    setOpportunityCreateOpen(false);
    message.success('商机已创建，请在商机字段中搜索选择');
    void fetchOpportunityOptions(undefined).then(setOpportunityOptions);
  };

  return (
    <>
      <Modal
        closable={{ 'aria-label': '关闭' }}
        keyboard
        open={open}
        title={title}
        width={FOLLOW_UP_MODAL_WIDTH}
        styles={{
          body: {
            maxHeight: 'calc(100vh - 220px)',
            overflowY: 'auto',
          },
        }}
        onCancel={handleCancel}
        onOk={() => void handleSubmit()}
        confirmLoading={confirmLoading || submitting}
        okButtonProps={{ disabled: uploading || detailLoading || detailError }}
        destroyOnHidden
        forceRender
      >
        <Form
          form={form}
          onValuesChange={() => {
            dirty.current = true;
          }}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name="followUpDate"
                label="跟进日期"
                rules={[{ required: true, message: '请选择跟进日期' }]}
              >
                <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="followUpTime" label="跟进时间">
                <TimePicker
                  format="HH:mm"
                  showSecond={false}
                  allowClear
                  placeholder="可选，精确到分钟"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="followUpType" label="跟进方式">
                <Select
                  style={{ width: '100%' }}
                  allowClear
                  options={
                    FOLLOW_UP_TYPE_OPTIONS as unknown as {
                      value: number;
                      label: string;
                    }[]
                  }
                  placeholder="请选择跟进方式"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="goalTag" label="跟进目标">
                <Select
                  style={{ width: '100%' }}
                  allowClear
                  options={
                    FOLLOW_UP_GOAL_OPTIONS as unknown as {
                      value: string;
                      label: string;
                    }[]
                  }
                  placeholder="请选择跟进目标"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="resultTag" label="跟进结果">
                <Select
                  style={{ width: '100%' }}
                  allowClear
                  options={
                    FOLLOW_UP_RESULT_OPTIONS as unknown as {
                      value: string;
                      label: string;
                    }[]
                  }
                  placeholder="请选择跟进结果"
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="content"
                label="跟进内容"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: '请输入跟进内容',
                  },
                  {
                    max: FOLLOW_UP_CONTENT_LIMIT,
                    message: '正文不能超过 100,000 字符',
                  },
                ]}
              >
                <MarkdownEditor
                  ref={contentEditor}
                  bizType={fileTarget.type}
                  bizUuid={fileTarget.uuid}
                  onBusyChange={setUploading}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="qualityScore" label="质量评分">
                <Select
                  style={{ width: '100%' }}
                  allowClear
                  options={
                    FOLLOW_UP_QUALITY_SCORE_OPTIONS as unknown as {
                      value: number;
                      label: string;
                    }[]
                  }
                  placeholder="请选择质量评分"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="nextStepConfirmed"
                label="是否已明确下一步"
                rules={[{ required: true, message: '请选择是否已明确下一步' }]}
              >
                <Select
                  style={{ width: '100%' }}
                  options={[
                    { label: '已明确下一步', value: true },
                    { label: '暂未明确下一步', value: false },
                  ]}
                  placeholder="请选择"
                />
              </Form.Item>
            </Col>
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) =>
                getFieldValue('nextStepConfirmed') !== false ? (
                  <>
                    <Col xs={24} md={8}>
                      <Form.Item
                        name="nextStepContent"
                        label="下一步动作"
                        rules={[
                          { required: true, message: '请输入下一步动作' },
                        ]}
                      >
                        <Input placeholder="请输入下一步动作" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="nextStepAt" label="下次动作时间">
                        <DatePicker
                          format="YYYY-MM-DD HH:mm"
                          showTime={{ format: 'HH:mm' }}
                          style={{ width: '100%' }}
                          placeholder="可选，安排下次动作时间"
                        />
                      </Form.Item>
                    </Col>
                  </>
                ) : null
              }
            </Form.Item>
            <Col xs={24} md={8}>
              <Form.Item name="nextTime" label="下次联系时间">
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="可选，安排下次联系时间"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="stageAdvanced" label="是否推动前进">
                <Select
                  style={{ width: '100%' }}
                  options={[
                    { label: '未明显推动前进', value: false },
                    { label: '已推动前进', value: true },
                  ]}
                  placeholder="请选择"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="keySignal" label="关键信号">
                <Input placeholder="可选，记录本次识别到的关键信号" />
              </Form.Item>
            </Col>
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) =>
                getFieldValue('resultTag') === 'INVALID' ? (
                  <Col xs={24} md={8}>
                    <Form.Item name="lostReasonHint" label="无效原因提示">
                      <Input placeholder="可选，记录无效原因或阻力原因" />
                    </Form.Item>
                  </Col>
                ) : null
              }
            </Form.Item>
            <Col xs={24} hidden={!selectedExtra?.length}>
              <Form.Item name="extraRelations" label="其他关联">
                <ExtraRelationTags />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="关联线索">
                <Space.Compact style={{ width: '100%' }}>
                  <Form.Item name="leadUuids" noStyle>
                    <Select
                      mode="multiple"
                      showSearch
                      filterOption={false}
                      options={leadOptions}
                      onSearch={(keyword) =>
                        void fetchLeadOptions(keyword).then((options) =>
                          setLeadOptions((prev) => mergeOptions(prev, options)),
                        )
                      }
                      placeholder="可选，关联线索"
                    />
                  </Form.Item>
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => setLeadCreateOpen(true)}
                  />
                </Space.Compact>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="关联客户">
                <Space.Compact style={{ width: '100%' }}>
                  <Form.Item name="customerUuids" noStyle>
                    <Select
                      mode="multiple"
                      showSearch
                      filterOption={false}
                      options={customerOptions}
                      onSearch={(keyword) =>
                        void fetchCustomerOptions(keyword).then((options) =>
                          setCustomerOptions((prev) =>
                            mergeOptions(prev, options),
                          ),
                        )
                      }
                      placeholder="可选，关联多个客户"
                    />
                  </Form.Item>
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => setCustomerCreateOpen(true)}
                  />
                </Space.Compact>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="关联联系人">
                <Space.Compact style={{ width: '100%' }}>
                  <Form.Item name="contactUuids" noStyle>
                    <Select
                      mode="multiple"
                      showSearch
                      filterOption={false}
                      options={contactOptions}
                      onSearch={(keyword) =>
                        void fetchContactOptions(keyword).then((options) =>
                          setContactOptions((prev) =>
                            mergeOptions(prev, options),
                          ),
                        )
                      }
                      placeholder="可选，关联联系人"
                    />
                  </Form.Item>
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => setContactCreateOpen(true)}
                  />
                </Space.Compact>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="关联商机">
                <Space.Compact style={{ width: '100%' }}>
                  <Form.Item name="opportunityUuids" noStyle>
                    <Select
                      mode="multiple"
                      showSearch
                      filterOption={false}
                      options={opportunityOptions}
                      onSearch={(keyword) =>
                        void fetchOpportunityOptions(keyword).then((options) =>
                          setOpportunityOptions((prev) =>
                            mergeOptions(prev, options),
                          ),
                        )
                      }
                      placeholder="可选，关联商机"
                    />
                  </Form.Item>
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => setOpportunityCreateOpen(true)}
                  />
                </Space.Compact>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="syncOpportunityAdvance"
                label="是否同步商机推进信息"
              >
                <Select
                  options={[
                    { label: '不同步商机推进信息', value: false },
                    { label: '同步回写商机推进信息', value: true },
                  ]}
                  placeholder="请选择是否同步商机推进信息"
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="createFollowUpTask" valuePropName="checked">
                <Checkbox>根据下一步动作同时创建后续任务</Checkbox>
              </Form.Item>
            </Col>
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) =>
                getFieldValue('syncOpportunityAdvance') ? (
                  <>
                    <Col xs={24} md={8}>
                      <Form.Item
                        name="syncNextAction"
                        label="同步回写的下一步动作"
                      >
                        <Input placeholder="默认会优先使用结构化的下一步动作" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={16}>
                      <Form.Item
                        name="syncCurrentBlockers"
                        label="同步回写的当前阻塞点"
                      >
                        <Input.TextArea
                          rows={3}
                          placeholder="可选，补充本次识别出的阻塞点"
                        />
                      </Form.Item>
                    </Col>
                  </>
                ) : null
              }
            </Form.Item>
          </Row>
        </Form>
      </Modal>
      <CustomerEditModal
        open={customerCreateOpen}
        onOpenChange={setCustomerCreateOpen}
        width={RELATED_MODAL_WIDTH}
        currentMemberUuid={currentMemberUuid}
        zIndex={1100}
        onSuccess={handleCustomerCreated}
      />
      <LeadEditModal
        open={leadCreateOpen}
        onOpenChange={setLeadCreateOpen}
        width={RELATED_MODAL_WIDTH}
        defaultCustomerUuid={(form.getFieldValue('customerUuids') || [])[0]}
        currentMemberUuid={currentMemberUuid}
        onSuccess={handleLeadCreated}
      />
      <ContactEditModal
        open={contactCreateOpen}
        onOpenChange={setContactCreateOpen}
        width={RELATED_MODAL_WIDTH}
        currentMemberUuid={currentMemberUuid}
        context={{
          customerUuid: (form.getFieldValue('customerUuids') || [])[0],
        }}
        zIndex={1100}
        onSuccess={handleContactCreated}
      />
      <OpportunityEditModal
        open={opportunityCreateOpen}
        onOpenChange={setOpportunityCreateOpen}
        width={RELATED_MODAL_WIDTH}
        currentMemberUuid={currentMemberUuid}
        context={{
          customerUuid: (form.getFieldValue('customerUuids') || [])[0],
        }}
        zIndex={1100}
        onSuccess={handleOpportunityCreated}
      />
    </>
  );
}
